import { App, type URLOpenListenerEvent } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { isNativePlatform } from './runtime';

const ALLOWED_HOSTS = new Set(['www.moviemeapp.com', 'moviemeapp.com', 'localhost']);

const ALLOWED_SCHEMES = new Set(['https:', 'http:', 'movieme:', 'capacitor:']);

/**
 * Paths the native shell may deep-link into.
 * Email confirm + password reset are intentionally excluded — those stay in
 * the system browser on moviemeapp.com (see AASA + auth remediation plan).
 */
const ALLOWED_PATH_PREFIXES = ['/privacy', '/terms', '/account', '/'];

/** Auth email paths must never render inside the Capacitor shell. */
const WEB_ONLY_AUTH_PATHS = ['/email-confirmed', '/reset-password'] as const;

export type NativeNavigationHandler = (pathWithSearchAndHash: string) => void;

function isAllowedMovieMeUrl(url: URL): boolean {
  if (!ALLOWED_SCHEMES.has(url.protocol)) return false;

  if (url.protocol === 'movieme:') {
    return true;
  }

  if (url.protocol === 'capacitor:') {
    return url.hostname === 'localhost' || url.hostname === '';
  }

  return ALLOWED_HOSTS.has(url.hostname);
}

export function normalizeNativePathname(url: URL): string {
  let pathname = url.pathname || '/';
  if (url.protocol === 'movieme:') {
    // movieme://home → host is the path segment in URL parsers
    if (!pathname || pathname === '/') {
      pathname = url.hostname ? `/${url.hostname}` : '/';
    }
  }
  return pathname;
}

export function isWebOnlyAuthPath(pathname: string): boolean {
  return WEB_ONLY_AUTH_PATHS.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

function pathAllowed(pathname: string): boolean {
  if (isWebOnlyAuthPath(pathname)) return false;
  return ALLOWED_PATH_PREFIXES.some(
    (prefix) =>
      pathname === prefix ||
      pathname.startsWith(`${prefix}/`) ||
      (prefix !== '/' && pathname.startsWith(prefix))
  );
}

/**
 * Reconstruct a canonical production HTTPS auth URL for stale Universal Link cache.
 * Preserves search + hash credentials. Rejects untrusted hosts and lookalike paths.
 */
export function toPublicAuthHttpsUrl(rawUrl: string): string | null {
  let url: URL;
  try {
    url = new URL(rawUrl);
  } catch {
    return null;
  }

  const pathname = normalizeNativePathname(url);
  if (!isWebOnlyAuthPath(pathname)) return null;

  if (url.protocol === 'https:' || url.protocol === 'http:') {
    if (url.hostname !== 'www.moviemeapp.com' && url.hostname !== 'moviemeapp.com') {
      return null;
    }
  } else if (url.protocol !== 'movieme:') {
    return null;
  }

  const https = new URL(`https://www.moviemeapp.com${pathname}`);
  https.search = url.search;
  https.hash = url.hash;
  return https.toString();
}

/**
 * Map an inbound native URL to an in-app React Router location.
 * Returns null when the URL must be ignored or handed off to the browser view.
 *
 * Return-to-app after web auth: `movieme://home` → `/`
 */
export function mapNativeOpenUrl(rawUrl: string): string | null {
  let url: URL;
  try {
    url = new URL(rawUrl);
  } catch {
    return null;
  }

  if (!isAllowedMovieMeUrl(url)) return null;

  const pathname = normalizeNativePathname(url);

  if (url.protocol === 'movieme:' && (pathname === '/' || pathname === '/home')) {
    return '/';
  }

  if (isWebOnlyAuthPath(pathname)) {
    return null;
  }

  if (!pathAllowed(pathname)) return null;

  return `${pathname}${url.search}${url.hash}`;
}

/**
 * Open an auth callback in SFSafariViewController via @capacitor/browser.
 * Never uses window.location (that can re-trigger a Universal Link loop).
 */
export async function handOffAuthUrlToBrowser(rawUrl: string): Promise<boolean> {
  const httpsUrl = toPublicAuthHttpsUrl(rawUrl);
  if (!httpsUrl) return false;
  try {
    await Browser.open({ url: httpsUrl });
    return true;
  } catch {
    return false;
  }
}

async function closeBrowserViewIfOpen(): Promise<void> {
  try {
    await Browser.close();
  } catch {
    // no browser view open
  }
}

const HANDLED_LAUNCH_KEY = 'movieme_handled_launch_url';

function alreadyHandled(rawUrl: string): boolean {
  try {
    return sessionStorage.getItem(HANDLED_LAUNCH_KEY) === rawUrl;
  } catch {
    return false;
  }
}

function markHandled(rawUrl: string): void {
  try {
    sessionStorage.setItem(HANDLED_LAUNCH_KEY, rawUrl);
  } catch {
    // ignore
  }
}

/**
 * Subscribe to Capacitor cold/warm app URL opens.
 * Stale auth Universal Links → Browser plugin once; movieme://home → `/`.
 */
export async function startNativeAppLinkHandling(
  navigate: NativeNavigationHandler
): Promise<() => void> {
  if (!isNativePlatform()) {
    return () => undefined;
  }

  const handle = async (rawUrl: string, fromLaunch: boolean) => {
    if (fromLaunch && alreadyHandled(rawUrl)) return;

    if (toPublicAuthHttpsUrl(rawUrl)) {
      if (fromLaunch) markHandled(rawUrl);
      await handOffAuthUrlToBrowser(rawUrl);
      return;
    }

    const mapped = mapNativeOpenUrl(rawUrl);
    if (!mapped) return;
    if (fromLaunch) markHandled(rawUrl);

    if (mapped === '/') {
      await closeBrowserViewIfOpen();
    }

    navigate(mapped);
  };

  try {
    const launch = await App.getLaunchUrl();
    if (launch?.url) await handle(launch.url, true);
  } catch {
    // no launch URL
  }

  const listener = await App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
    if (event?.url) void handle(event.url, false);
  });

  return () => {
    listener.remove();
  };
}
